import { z } from "zod";
import { GAMES, MATCH_RESULTS } from "../domain/games";

/** Win/loss/draw counters for one game (or, aggregated, across all games). */
export const gameStatSchema = z.object({
  gamesPlayed: z.number().int().nonnegative(),
  wins: z.number().int().nonnegative(),
  losses: z.number().int().nonnegative(),
  /**
   * A draw is a game PLAYED that is neither a win nor a loss, so without it
   * `gamesPlayed - wins - losses` is an unexplained remainder wherever the three are shown.
   * DEFAULTED, so a client or a server that predates the column still parses.
   */
  draws: z.number().int().nonnegative().default(0),
});
export type GameStat = z.infer<typeof gameStatSchema>;

/**
 * A player's stats: aggregate totals plus a per-game breakdown. Every game in
 * `GAMES` is always present (zero-filled until the game-server writes results),
 * so clients can render a stable grid without null checks.
 */
export const perGameStatsSchema = z.object({
  hokm: gameStatSchema,
  shelem: gameStatSchema,
  pasur: gameStatSchema,
  hearts: gameStatSchema,
  bluff: gameStatSchema,
  chess: gameStatSchema,
  backgammon: gameStatSchema,
  mensch: gameStatSchema,
  dooz: gameStatSchema,
  ladders: gameStatSchema,
});
export type PerGameStats = z.infer<typeof perGameStatsSchema>;

export const userStatsSchema = z.object({
  gamesPlayed: z.number().int().nonnegative(),
  wins: z.number().int().nonnegative(),
  losses: z.number().int().nonnegative(),
  draws: z.number().int().nonnegative().default(0),
  perGame: perGameStatsSchema,
});
export type UserStats = z.infer<typeof userStatsSchema>;

/** A single finished match, from the caller's perspective. */
export const matchHistoryItemSchema = z.object({
  id: z.string(),
  game: z.enum(GAMES),
  result: z.enum(MATCH_RESULTS),
  /** Free-form per-game score payload (e.g. `{ me: 100, them: 60 }`); may be null. */
  score: z.unknown().nullable(),
  /** ISO-8601 timestamp of when the match ended. */
  endedAt: z.string(),
});
export type MatchHistoryItem = z.infer<typeof matchHistoryItemSchema>;

/** One page of match history; `nextCursor` is the id to pass back for the next page. */
export const matchHistoryPageSchema = z.object({
  items: z.array(matchHistoryItemSchema),
  nextCursor: z.string().nullable(),
});
export type MatchHistoryPage = z.infer<typeof matchHistoryPageSchema>;

const ZERO: GameStat = { gamesPlayed: 0, wins: 0, losses: 0, draws: 0 };

/** A fresh all-zero stats object (new copies of every nested counter). */
export function zeroUserStats(): UserStats {
  return {
    gamesPlayed: 0,
    wins: 0,
    losses: 0,
    draws: 0,
    perGame: {
      hokm: { ...ZERO },
      shelem: { ...ZERO },
      pasur: { ...ZERO },
      hearts: { ...ZERO },
      bluff: { ...ZERO },
      chess: { ...ZERO },
      backgammon: { ...ZERO },
      mensch: { ...ZERO },
      dooz: { ...ZERO },
      ladders: { ...ZERO },
    },
  };
}
